"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";

import { MEMORY_FACTS } from "./content";
import { clamp, easeInOut, fitCanvas, prefersReducedMotion } from "./motion";
import { useSectionInView } from "./section";

/**
 * "The memory that grows": a graph that fills in over eleven seconds, holds
 * for four, and starts again — six of its nodes are facts, and a chip with the
 * fact appears beside each as it is learned. The graph is deterministic
 * (seeded) so it looks the same on every visit. Under reduced motion the
 * finished graph is shown. A light card in both themes (like the phone), so
 * its colours are literal.
 */

const N = 64;
const GROW_S = 11;
const HOLD_S = 4;
const FACT_NODES: readonly number[] = [6, 14, 23, 33, 44, 56];
const CHIP_SLOTS: readonly (readonly [number, number])[] = [
  [0.28, 0.07],
  [0.72, 0.2],
  [0.26, 0.34],
  [0.74, 0.47],
  [0.3, 0.6],
  [0.7, 0.73],
];
const TAU = Math.PI * 2;

interface Node {
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly z: number;
}

interface Point {
  readonly x: number;
  readonly y: number;
}

// Park–Miller, seeded, so the layout is fixed.
const rng = { seed: 7 };
const rnd = (): number => {
  rng.seed = (rng.seed * 16807) % 2147483647;
  return (rng.seed - 1) / 2147483646;
};
const dist2 = (a: Node, b: Node): number => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

const raw: readonly Node[] = Array.from({ length: N }, () => {
  const a = rnd() * TAU;
  const rr = Math.pow(rnd(), 0.65) * 0.44;
  return { x: 0.5 + Math.cos(a) * rr * 0.95, y: 0.46 + Math.sin(a) * rr * 0.95, r: rr, z: rnd() };
});
const NODES: readonly Node[] = [...raw]
  .sort((a, b) => a.r - b.r)
  .map((n, i) => (i === 0 ? { ...n, x: 0.5, y: 0.47 } : n));
/** [from, to] with to > from, so an edge appears with its later node. */
const EDGES: readonly (readonly [number, number])[] = NODES.flatMap((n, i) => {
  if (i === 0) return [];
  const earlier = NODES.slice(0, i);
  const nearest = earlier.reduce(
    (best, candidate, j) => (dist2(n, candidate) < dist2(n, earlier[best] ?? candidate) ? j : best),
    0,
  );
  const extra = earlier.flatMap((p, j) =>
    j !== nearest && dist2(n, p) < 0.006 && rnd() < 0.5 ? [[j, i] as const] : [],
  );
  return [[nearest, i] as const, ...extra];
});

export function MemoryGraph() {
  const { i18n } = useLingui();
  const inView = useSectionInView();
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const barRef = useRef<HTMLElement>(null);
  const chipRefs = useRef<(HTMLSpanElement | null)[]>([]);
  // The chip anchors feed both the DOM chips (state) and the dashed lines the
  // canvas draws to them (a ref the draw loop reads), so a re-layout does not
  // restart the animation.
  const anchors = useRef<Point[]>([]);
  const [chipPos, setChipPos] = useState<Point[]>([]);
  const [litCount, setLitCount] = useState(0);

  // Chip placement: the fixed slots, clamped so a chip never leaves the box.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const place = () => {
      const bw = box.clientWidth;
      const bh = box.clientHeight;
      const placed = CHIP_SLOTS.map(([sx, sy], k) => {
        const chip = chipRefs.current[k];
        const hw = (chip?.offsetWidth ?? 120) / 2 + 12;
        return { x: clamp(sx * bw, hw, bw - hw), y: sy * bh + (chip?.offsetHeight ?? 28) * 1.3 };
      });
      anchors.current = placed;
      setChipPos(placed);
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(box);
    document.fonts.ready.then(place).catch(() => undefined);
    return () => {
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduced = prefersReducedMotion();
    const state = { W: 0, H: 0, raf: 0, t0: performance.now(), lit: -1, glow: ctx.createRadialGradient(0, 0, 0, 0, 0, 1) };

    const draw = (p: number, t: number) => {
      const { W, H } = state;
      ctx.clearRect(0, 0, W, H);
      const shown = Math.max(1, Math.floor(1 + p * (N - 1)));
      EDGES.forEach(([from, to]) => {
        if (to >= shown) return;
        const a = NODES[from];
        const b = NODES[to];
        if (!a || !b) return;
        const age = clamp((p * (N - 1) - to + 1) / 3, 0, 1);
        ctx.strokeStyle = `rgba(0,134,191,${0.22 * age})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x * W, a.y * H);
        ctx.lineTo(b.x * W, b.y * H);
        ctx.stroke();
      });
      NODES.slice(0, shown).forEach((n, i) => {
        const age = clamp((p * (N - 1) - i + 1) / 2, 0, 1);
        const isFact = FACT_NODES.includes(i);
        const radius =
          (isFact ? 5 : 2 + n.z * 2.2) * (0.4 + 0.6 * age) + (isFact && !reduced ? Math.sin(t * 2 + i) * 0.6 : 0);
        ctx.fillStyle =
          i === 0 ? "#E8725A" : isFact ? `rgba(232,114,90,${0.9 * age})` : `rgba(10,22,40,${(0.25 + 0.4 * n.z) * age})`;
        ctx.beginPath();
        ctx.arc(n.x * W, n.y * H, radius, 0, TAU);
        ctx.fill();
      });
      const root = NODES[0];
      if (root) {
        ctx.fillStyle = state.glow;
        ctx.beginPath();
        ctx.arc(root.x * W, root.y * H, 60, 0, TAU);
        ctx.fill();
      }
      const lit = FACT_NODES.filter((f) => shown > f).length;
      FACT_NODES.slice(0, lit).forEach((f, k) => {
        const anchor = anchors.current[k];
        const n = NODES[f];
        if (!anchor || !n) return;
        ctx.strokeStyle = "rgba(232,114,90,0.45)";
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.moveTo(anchor.x, anchor.y - 2);
        ctx.lineTo(n.x * W, n.y * H);
        ctx.stroke();
        ctx.setLineDash([]);
      });
      // Per-frame DOM writes, not state: the bar moves 60 times a second and
      // the chips only change six times a cycle.
      if (barRef.current) barRef.current.style.width = `${(p * 100).toFixed(1)}%`;
      if (lit !== state.lit) {
        state.lit = lit;
        setLitCount(lit);
      }
    };

    const frame = (now: number) => {
      const t = (now - state.t0) / 1000;
      const inCycle = t % (GROW_S + HOLD_S);
      draw(inCycle < GROW_S ? easeInOut(inCycle / GROW_S) : 1, t);
      state.raf = requestAnimationFrame(frame);
    };

    const size = () => {
      const { width, height } = fitCanvas(canvas, ctx);
      state.W = width;
      state.H = height;
      const root = NODES[0];
      const rx = (root?.x ?? 0.5) * width;
      const ry = (root?.y ?? 0.5) * height;
      state.glow = ctx.createRadialGradient(rx, ry, 0, rx, ry, 60);
      state.glow.addColorStop(0, "rgba(232,114,90,0.35)");
      state.glow.addColorStop(1, "rgba(232,114,90,0)");
      if (!inView || reduced) draw(reduced ? 1 : 0, 0);
    };

    size();
    window.addEventListener("resize", size);
    if (inView && !reduced) state.raf = requestAnimationFrame(frame);
    return () => {
      window.removeEventListener("resize", size);
      cancelAnimationFrame(state.raf);
    };
  }, [inView]);

  return (
    <div
      ref={boxRef}
      aria-hidden="true"
      className="relative aspect-[0.9] w-full overflow-hidden rounded-3xl bg-[#FAFAF8] shadow-[0_0_0_1px_rgba(10,22,40,0.12),0_40px_80px_-50px_rgba(10,22,40,0.35)] sm:aspect-[1.15]"
    >
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      {MEMORY_FACTS.map((fact, k) => (
        <span
          key={fact.id}
          ref={(el) => {
            chipRefs.current[k] = el;
          }}
          className={`absolute whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-[13px] font-medium text-[#0A1628] shadow-[0_0_0_1px_rgba(10,22,40,0.12),0_8px_20px_-10px_rgba(10,22,40,0.3)] transition-[opacity,transform] duration-700 motion-reduce:transition-none ${
            k < litCount ? "opacity-100" : "opacity-0"
          }`}
          style={{
            left: chipPos[k]?.x ?? 0,
            top: chipPos[k]?.y ?? 0,
            transform: `translate(-50%, -130%) scale(${k < litCount ? 1 : 0.9})`,
          }}
        >
          {i18n._(fact)}
        </span>
      ))}
      <div className="absolute inset-x-5 bottom-11 h-0.5 overflow-hidden rounded bg-[#0A1628]/12">
        <i ref={barRef} className="block h-full w-0 bg-[#0086BF]" />
      </div>
      <div className="absolute inset-x-5 bottom-4 flex justify-between text-[13px] text-[#5E6878]">
        <span>
          <Trans>Week 1</Trans>
        </span>
        <span>
          <Trans>Week 5</Trans>
        </span>
      </div>
    </div>
  );
}
